import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { inventarioService } from '../../services/inventarioService';
import { productosService } from '../../services/productosService';
import { sucursalesService } from '../../services/sucursalesService';

// Tipos de movimiento (hardcodeados por ahora - TODO: crear endpoint)
const TIPOS_MOVIMIENTO = [
  { TipoMovimientoID: 1, Nombre: 'Entrada - Compra', EsEntrada: true },
  { TipoMovimientoID: 2, Nombre: 'Entrada - Devolución', EsEntrada: true },
  { TipoMovimientoID: 3, Nombre: 'Entrada - Ajuste', EsEntrada: true },
  { TipoMovimientoID: 4, Nombre: 'Salida - Venta', EsEntrada: false },
  { TipoMovimientoID: 5, Nombre: 'Salida - Ajuste', EsEntrada: false },
  { TipoMovimientoID: 6, Nombre: 'Transferencia', EsEntrada: false },
];

function MovimientoForm({ sucursalId, usuarioId, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    tipoMovimientoID: '',
    productoID: '',
    sucursalOrigenID: sucursalId || '',
    sucursalDestinoID: '',
    cantidad: 0,
    observaciones: '',
    esTransferencia: false,
  });

  const [productos, setProductos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stockInfo, setStockInfo] = useState(null);

  useEffect(() => {
    if (sucursalId) {
      loadProductos();
      loadSucursales();
    }
  }, [sucursalId]);

  const loadProductos = async () => {
    try {
      // Obtener productos filtrados por sucursal (empresaId null para obtener todos)
      const response = await productosService.getAll(null, sucursalId, null, null);
      if (response.success && response.data) {
        const productosData = Array.isArray(response.data) ? response.data : [];
        const normalized = productosData.map(prod => ({
          ProductoID: prod.ProductoID || prod.productoID,
          Nombre: prod.Nombre || prod.nombre || '',
          SKU: prod.SKU || prod.sku || '',
        }));
        setProductos(normalized);
      }
    } catch (err) {
    }
  };

  const loadSucursales = async () => {
    try {
      // Obtener todas las sucursales para transferencias (sin filtro de empresa para obtener todas)
      const response = await sucursalesService.getAll(null);
      if (response.success && response.data) {
        const sucursalesData = Array.isArray(response.data) ? response.data : [];
        const normalized = sucursalesData
          .filter(suc => (suc.SucursalID || suc.sucursalID) !== sucursalId)
          .map(suc => ({
            SucursalID: suc.SucursalID || suc.sucursalID,
            Nombre: suc.Nombre || suc.nombre || '',
          }));
        setSucursales(normalized);
      }
    } catch (err) {
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.tipoMovimientoID) newErrors.tipoMovimientoID = 'El tipo de movimiento es requerido';
    if (!formData.productoID) newErrors.productoID = 'El producto es requerido';
    if (!formData.sucursalOrigenID) newErrors.sucursalOrigenID = 'La sucursal origen es requerida';
    if (formData.esTransferencia && !formData.sucursalDestinoID) {
      newErrors.sucursalDestinoID = 'La sucursal destino es requerida para transferencias';
    }
    if (!formData.cantidad || formData.cantidad <= 0) {
      newErrors.cantidad = 'La cantidad debe ser mayor a 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const tipoMovimiento = TIPOS_MOVIMIENTO.find(t => t.TipoMovimientoID === parseInt(formData.tipoMovimientoID));
      const esTransferencia = tipoMovimiento?.Nombre === 'Transferencia' || formData.esTransferencia;

      const movimientoData = {
        TipoMovimientoID: parseInt(formData.tipoMovimientoID),
        ProductoID: parseInt(formData.productoID),
        SucursalOrigenID: parseInt(formData.sucursalOrigenID),
        SucursalDestinoID: esTransferencia && formData.sucursalDestinoID ? parseInt(formData.sucursalDestinoID) : null,
        Cantidad: parseFloat(formData.cantidad),
        UsuarioID: usuarioId,
        Observaciones: formData.observaciones?.trim() || null,
      };

      let response;
      if (esTransferencia) {
        response = await inventarioService.registrarTransferencia(movimientoData);
      } else {
        response = await inventarioService.registrarMovimiento(movimientoData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al registrar el movimiento');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleProductoChange = async (e) => {
    const productoID = e.target.value;
    handleChange(e);
    
    if (productoID && formData.sucursalOrigenID) {
      try {
        const response = await inventarioService.getStock(
          parseInt(productoID),
          parseInt(formData.sucursalOrigenID)
        );
        if (response.success && response.data) {
          setStockInfo(response.data);
        } else {
          setStockInfo(null);
        }
      } catch (err) {
        setStockInfo(null);
      }
    } else {
      setStockInfo(null);
    }
  };

  const calcularStockResultante = () => {
    if (!stockInfo || !formData.cantidad) return null;
    const tipoMovimiento = TIPOS_MOVIMIENTO.find(t => t.TipoMovimientoID === parseInt(formData.tipoMovimientoID));
    if (!tipoMovimiento) return null;
    
    const stockActual = stockInfo.StockActual !== undefined ? stockInfo.StockActual : (stockInfo.stockActual !== undefined ? stockInfo.stockActual : 0);
    const stockMinimo = stockInfo.StockMinimo !== undefined ? stockInfo.StockMinimo : (stockInfo.stockMinimo !== undefined ? stockInfo.stockMinimo : 0);
    const cantidad = parseFloat(formData.cantidad) || 0;
    
    if (tipoMovimiento.EsEntrada) {
      return stockActual + cantidad;
    } else {
      return stockActual - cantidad;
    }
  };

  const validarStockMinimo = () => {
    if (!stockInfo || !formData.cantidad || !formData.tipoMovimientoID) return false;
    const tipoMovimiento = TIPOS_MOVIMIENTO.find(t => t.TipoMovimientoID === parseInt(formData.tipoMovimientoID));
    if (!tipoMovimiento || tipoMovimiento.EsEntrada) return false;
    
    const stockResultante = calcularStockResultante();
    const stockMinimo = stockInfo.StockMinimo !== undefined ? stockInfo.StockMinimo : (stockInfo.stockMinimo !== undefined ? stockInfo.stockMinimo : 0);
    
    return stockResultante !== null && stockResultante <= stockMinimo;
  };

  const handleTipoMovimientoChange = async (e) => {
    const tipoMovimientoID = parseInt(e.target.value);
    const tipoMovimiento = TIPOS_MOVIMIENTO.find(t => t.TipoMovimientoID === tipoMovimientoID);
    const esTransferencia = tipoMovimiento?.Nombre === 'Transferencia';

    setFormData((prev) => ({
      ...prev,
      tipoMovimientoID: tipoMovimientoID.toString(),
      esTransferencia,
      sucursalDestinoID: esTransferencia ? prev.sucursalDestinoID : '',
    }));

    // Recargar stock info si hay un producto seleccionado
    if (formData.productoID && formData.sucursalOrigenID) {
      try {
        const response = await inventarioService.getStock(
          parseInt(formData.productoID),
          parseInt(formData.sucursalOrigenID)
        );
        if (response.success && response.data) {
          setStockInfo(response.data);
        }
      } catch (err) {
      }
    }
  };

  return (
    <Modal isOpen={true} toggle={onCancel} size="lg" className="modal-black">
      <ModalHeader toggle={onCancel}>Nuevo Movimiento de Inventario</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row>
            <Col md="6">
              <FormGroup>
                <Label>
                  Tipo de Movimiento <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="tipoMovimientoID"
                  value={formData.tipoMovimientoID}
                  onChange={handleTipoMovimientoChange}
                  invalid={!!errors.tipoMovimientoID}
                  required
                >
                  <option value="">Seleccione un tipo</option>
                  {TIPOS_MOVIMIENTO.map((tipo) => (
                    <option key={tipo.TipoMovimientoID} value={tipo.TipoMovimientoID}>
                      {tipo.Nombre}
                    </option>
                  ))}
                </Input>
                {errors.tipoMovimientoID && (
                  <div className="text-danger small">{errors.tipoMovimientoID}</div>
                )}
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label>
                  Producto <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="productoID"
                  value={formData.productoID}
                  onChange={handleProductoChange}
                  invalid={!!errors.productoID}
                  required
                >
                  <option value="">Seleccione un producto</option>
                  {productos.map((prod) => (
                    <option key={prod.ProductoID} value={prod.ProductoID}>
                      {prod.Nombre} ({prod.SKU})
                    </option>
                  ))}
                </Input>
                {errors.productoID && (
                  <div className="text-danger small">{errors.productoID}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label>
                  Sucursal Origen <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  value={sucursalId ? `Sucursal ID: ${sucursalId}` : 'No seleccionada'}
                  disabled
                />
              </FormGroup>
            </Col>

            {formData.esTransferencia && (
              <Col md="6">
                <FormGroup>
                  <Label>
                    Sucursal Destino <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    name="sucursalDestinoID"
                    value={formData.sucursalDestinoID}
                    onChange={handleChange}
                    invalid={!!errors.sucursalDestinoID}
                    required={formData.esTransferencia}
                  >
                    <option value="">Seleccione una sucursal</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </Input>
                  {errors.sucursalDestinoID && (
                    <div className="text-danger small">{errors.sucursalDestinoID}</div>
                  )}
                </FormGroup>
              </Col>
            )}
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label>
                  Cantidad <span className="text-danger">*</span>
                </Label>
                <Input
                  type="number"
                  name="cantidad"
                  value={formData.cantidad || ''}
                  onChange={handleChange}
                  onFocus={(e) => e.target.select()}
                  min="0.01"
                  step="0.01"
                  invalid={!!errors.cantidad || validarStockMinimo()}
                  required
                />
                {errors.cantidad && (
                  <div className="text-danger small">{errors.cantidad}</div>
                )}
                {validarStockMinimo() && (
                  <div className="text-danger small mt-1">
                    <strong>⚠ Advertencia:</strong> El stock resultante ({calcularStockResultante()?.toFixed(2) || 0}) sería menor o igual al stock mínimo ({stockInfo?.StockMinimo || stockInfo?.stockMinimo || 0}). Esta operación no está permitida.
                  </div>
                )}
                {stockInfo && formData.cantidad && !validarStockMinimo() && (
                  <small className="form-text text-muted">
                    Stock actual: {stockInfo.StockActual !== undefined ? stockInfo.StockActual : (stockInfo.stockActual !== undefined ? stockInfo.stockActual : 0)} | 
                    Stock mínimo: {stockInfo.StockMinimo !== undefined ? stockInfo.StockMinimo : (stockInfo.stockMinimo !== undefined ? stockInfo.stockMinimo : 0)} | 
                    Stock resultante: {calcularStockResultante()?.toFixed(2) || 0}
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Observaciones</Label>
                <Input
                  type="textarea"
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange}
                  rows="3"
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={loading || validarStockMinimo()}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default MovimientoForm;

